import { LinkButton } from '@understory-io/leaf/link-button';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage } from 'react-intl';
import { FormattedPrice } from '~/ui/formatted';

type BookingBarProps = {
  /** Bare checkout-entry URL for this experience, built server-side so it is
   *  cross-domain (the owner's storefront) for shared/connected experiences and
   *  same-domain otherwise. The entry dispatches into whichever flow the
   *  experience uses, so the bar needs no flow-specific knowledge. */
  checkoutHref: string;
  /** Whether the price carries the "From" prefix — the storefront's
   *  `showFromPrice` preference. Controls only the prefix; the price itself
   *  renders whenever it is available. */
  showFromPrice: boolean;
  defaultPrice?: {
    amountCents: number;
    vatExclusiveAmountCents: number;
    currency: string;
    exponent: number;
    vatExempt: boolean;
  } | null;
  /** No-dates state (US-10809): the bar drops the price and its CTA becomes a
   *  full-width "Show dates" link — the checkout page it opens shows the
   *  waitlist rather than a calendar. */
  isEmpty?: boolean;
  /** Runs on tap, before the navigation to checkout. */
  onClick?: () => void;
};

/**
 * Mobile-only floating booking bar. The far simpler counterpart to the tablet+
 * booking panels: no calendar, list, filters or availability — a price and a
 * single CTA that goes straight to checkout (the panel's CTA opens the booking
 * dialog instead). The parent gates its viewport visibility via a wrapper.
 *
 * In the no-dates `isEmpty` state there is nothing priced to book, so the bar
 * drops the price and shows a full-width "Show dates" CTA; the checkout page it
 * opens renders the waitlist instead of a calendar.
 */
export function BookingBar({
  checkoutHref,
  showFromPrice,
  defaultPrice,
  isEmpty = false,
  onClick,
}: BookingBarProps) {
  const formattedDefaultPrice = defaultPrice ? (
    <FormattedPrice
      price={{
        vatInclusivePriceCents: defaultPrice.amountCents,
        vatExclusivePriceCents: defaultPrice.vatExclusiveAmountCents,
        currency: defaultPrice.currency,
        exponent: defaultPrice.exponent,
        vatExempt: defaultPrice.vatExempt,
      }}
    />
  ) : null;

  return (
    <div className={barStyles}>
      {!isEmpty && formattedDefaultPrice && (
        <Text style="subheadline-small" color="primary">
          {showFromPrice ? (
            <FormattedMessage
              defaultMessage="From {price}"
              description="Default price label on the mobile floating booking bar, with 'From' prefix"
              values={{ price: formattedDefaultPrice }}
            />
          ) : (
            formattedDefaultPrice
          )}
        </Text>
      )}
      {/* Straight to the bare checkout entry for both flows: the entry route
          decides which step comes first, and for an open time slot that decision
          depends on basket state (are the tickets already picked?) — something
          only the server knows. The bar hands over no selection of its own
          either, since the duration + tickets widget is the tablet+ panel only.
          No `?from=` anchor: the scheduled flow's calendar self-selects the first
          available date server-side, and adding one would skip step 1 (a
          date-only deep link routes straight to the time step). In the empty
          state the page this opens shows the waitlist instead. */}
      <LinkButton
        href={checkoutHref}
        appearance="primary"
        size="standard"
        onClick={onClick}
        // The empty state drops the price, so the CTA takes the full bar width.
        className={isEmpty ? css({ flexGrow: '1' }) : undefined}
      >
        {isEmpty ? (
          <FormattedMessage
            defaultMessage="Show dates"
            description="Mobile floating booking bar CTA for a no-dates experience; opens the checkout page which offers the waitlist"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Book now"
            description="Mobile floating booking bar primary call-to-action"
          />
        )}
      </LinkButton>
    </div>
  );
}

const barStyles = css({
  display: 'flex',
  position: 'fixed',
  bottom: 'small',
  left: 'small',
  right: 'small',
  zIndex: '[100]',
  background: 'surface.raised',
  border: 'small',
  borderColor: 'border.neutral',
  borderRadius: 'xlarge',
  padding: 'large',
  shadow: 'large',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'medium',
});
